import { FileQuestion, MessageSquareWarning } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { StatusBanner } from '../components/StatusBanner.js';
import { AlfPerformanceSection } from '../components/faq-monitor/AlfPerformanceSection.js';
import { SEVERITY_FILTERS, VERDICT_FILTERS } from '../components/faq-monitor/SeverityBadges.js';
import {
  ArticleGroup,
  CoverageItem,
  groupByArticle,
} from '../components/faq-monitor/WorklistItems.js';
import { isActiveDisposition, isStale, useFaqWorklist } from '../hooks/useFaqMonitor.js';
import type { FaqFreshness, FaqSeverity, FaqSummary, FaqVerdict } from '../types/faq-monitor.js';

/**
 * 画面上部のサマリー。
 *
 * 数字を等しく6個並べると、最初に見るべき値が決まらない。
 * 「そのまま従うと手続きが失敗する」重大度 高 を主役に置き、残りは従属させる。
 * 母数は対応方針で隠した指摘も含めた値を API から受け取り、そのまま出す。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 最優先で直すべき件数を主役に、対象記事数や論点数を従属情報として表示する
 * @graph-connects none
 */
export function SummarySection({ summary }: { summary: FaqSummary }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-4 rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3">
      <p className="text-3xl leading-none font-bold tabular-nums text-red-600 dark:text-red-400">
        {summary.severityHigh}
      </p>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground">{t('faqMonitor.summaryHighLead')}</p>
        <p className="text-xs text-foreground-secondary">{t('faqMonitor.summaryHighDesc')}</p>
      </div>
      <div className="ml-auto flex flex-col items-end text-xs tabular-nums text-foreground-secondary">
        <span>
          {t('faqMonitor.summaryRestSeverity', {
            medium: summary.severityMedium,
            low: summary.severityLow,
          })}
        </span>
        <span>
          {t('faqMonitor.summaryRestScope', {
            articles: summary.articleCount,
            findings: summary.findingCount,
          })}
        </span>
        <span>
          {t('faqMonitor.summaryRestCoverage', {
            topics: summary.topicCount,
            inquiries: summary.inquiryCount,
          })}
        </span>
      </div>
    </div>
  );
}

/**
 * 絞り込みボタン1つ。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 絞り込み条件を選ぶボタンを、選択状態が分かる見た目で表示する
 * @graph-connects none
 */
export function FilterButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full border px-3 py-1 text-xs font-medium ${
        active
          ? 'border-transparent bg-foreground text-background'
          : 'border-border bg-background text-foreground-secondary'
      }`}
    >
      {label}
    </button>
  );
}

/**
 * 判定がいつ時点かを示す帯（US-7）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 判定の最終実行日と、止まっている場合の警告を表示する
 * @graph-connects StatusBanner [calls] 帯の見た目
 */
export function FreshnessBanner({ freshness, label }: { freshness: FaqFreshness; label: string }) {
  const { t } = useTranslation();
  const stale = isStale(freshness);

  return (
    <StatusBanner
      tone={stale ? 'warning' : 'ok'}
      message={
        freshness.runDate === null
          ? t('faqMonitor.neverRun', { label })
          : t('faqMonitor.lastRun', { label, date: freshness.runDate })
      }
      emphasis={
        stale && freshness.staleDays !== null
          ? t('faqMonitor.staleWarning', { days: freshness.staleDays })
          : undefined
      }
    />
  );
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business FAQ監視の作業リスト。実装仕様と食い違っている記事を記事ごとにまとめて重い順に、顧客に聞かれているのに書かれていない／伝わっていない論点を件数順に出し、ALFが引用した記事ごとの完結率を完結率の低い順に出し、対応方針の管理と過去スナップショットの参照を提供する。
 * @graph-connects useFaqWorklist [calls] 作業リスト取得
 * @graph-connects groupByArticle [calls] 指摘の記事ごとの束ね
 * @graph-connects ArticleGroup [calls] 記事ごとの束ねブロックの表示
 * @graph-connects CoverageItem [calls] カバレッジ論点1件の表示
 * @graph-connects AlfPerformanceSection [calls] ALF実績セクションの表示
 * @graph-connects isActiveDisposition [calls] 既定表示に残す対応方針の判定
 */
export function FaqMonitorPage() {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);
  const [showResolved, setShowResolved] = useState(false);
  const [severityFilter, setSeverityFilter] = useState<FaqSeverity | null>(null);
  const [verdictFilter, setVerdictFilter] = useState<FaqVerdict | null>(null);
  const { data, isLoading, error } = useFaqWorklist(selectedDate);

  if (isLoading) {
    return <p className="p-6 text-foreground-secondary">{t('faqMonitor.loading')}</p>;
  }

  if (error || !data) {
    return (
      <p className="p-6 text-red-600 dark:text-red-400" role="alert">
        {t('faqMonitor.loadError')}
      </p>
    );
  }

  const visibleDiagnostics = data.diagnostics
    .filter((d) => showResolved || isActiveDisposition(d.disposition.status))
    .filter((d) => severityFilter === null || d.severity === severityFilter)
    .filter((d) => verdictFilter === null || d.verdict === verdictFilter);
  const visibleCoverage = data.coverage.filter(
    (c) => showResolved || isActiveDisposition(c.disposition.status),
  );
  const groups = groupByArticle(visibleDiagnostics);

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground">{t('faqMonitor.title')}</h1>
          <p className="mt-1 text-sm text-foreground-secondary">{t('faqMonitor.description')}</p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-foreground-muted">{t('faqMonitor.snapshotLabel')}</span>
          <select
            value={data.runDate ?? ''}
            onChange={(e) => {
              setSelectedDate(e.target.value === '' ? undefined : e.target.value);
            }}
            className="rounded border border-border bg-background px-2 py-1 text-foreground"
          >
            {data.snapshotDates.map((snapshot) => (
              <option key={snapshot.runDate} value={snapshot.runDate}>
                {t('faqMonitor.snapshotOption', {
                  date: snapshot.runDate,
                  count: snapshot.findingCount,
                })}
              </option>
            ))}
          </select>
        </label>
      </div>

      <SummarySection summary={data.summary} />

      <Card className="space-y-3 p-4">
        <div className="flex items-center gap-2">
          <FileQuestion className="h-5 w-5 text-foreground-muted" aria-hidden="true" />
          <h2 className="font-semibold text-foreground">{t('faqMonitor.diagnosticsHeading')}</h2>
        </div>
        <FreshnessBanner
          freshness={data.diagnosticsFreshness}
          label={t('faqMonitor.diagnosticsLabel')}
        />

        <div className="flex flex-wrap items-center gap-2">
          <FilterButton
            label={t('faqMonitor.filterAll')}
            active={severityFilter === null && verdictFilter === null}
            onClick={() => {
              setSeverityFilter(null);
              setVerdictFilter(null);
            }}
          />
          {SEVERITY_FILTERS.map((severity) => (
            <FilterButton
              key={severity}
              label={severity}
              active={severityFilter === severity}
              onClick={() => {
                setSeverityFilter(severityFilter === severity ? null : severity);
              }}
            />
          ))}
          {VERDICT_FILTERS.map((verdict) => (
            <FilterButton
              key={verdict}
              label={verdict}
              active={verdictFilter === verdict}
              onClick={() => {
                setVerdictFilter(verdictFilter === verdict ? null : verdict);
              }}
            />
          ))}
          <FilterButton
            label={t('faqMonitor.showResolved')}
            active={showResolved}
            onClick={() => {
              setShowResolved(!showResolved);
            }}
          />
        </div>

        {groups.length === 0 ? (
          <p className="py-4 text-sm text-foreground-secondary">{t('faqMonitor.noDiagnostics')}</p>
        ) : (
          <div className="space-y-2">
            {groups.map((group) => (
              <ArticleGroup
                key={group.articleId}
                articleTitle={group.articleTitle}
                diagnostics={group.diagnostics}
              />
            ))}
          </div>
        )}
      </Card>

      <Card className="space-y-3 p-4">
        <div className="flex items-center gap-2">
          <MessageSquareWarning className="h-5 w-5 text-foreground-muted" aria-hidden="true" />
          <h2 className="font-semibold text-foreground">{t('faqMonitor.coverageHeading')}</h2>
        </div>
        <FreshnessBanner freshness={data.coverageFreshness} label={t('faqMonitor.coverageLabel')} />
        {visibleCoverage.length === 0 ? (
          <p className="py-4 text-sm text-foreground-secondary">{t('faqMonitor.noCoverage')}</p>
        ) : (
          <ul>
            {visibleCoverage.map((topic) => (
              <CoverageItem key={topic.topicHash} topic={topic} />
            ))}
          </ul>
        )}
      </Card>

      <AlfPerformanceSection />
    </div>
  );
}
